import {
  Box,
  Grid,
  IconLabel,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountPlusIcon,
  DomainIcon,
  EmailIcon,
  InformationIcon,
  PhoneIcon,
} from '@atgfr8/icons';
import {
  BasicModal,
  Button,
  ContactDropdown,
  Notification,
  ToastType,
} from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal, openModal } from '@store/modals';
import {
  addNewContactToCarrier,
  addNewContactToCarrierStoreAndUpdateOrderStore,
  Carrier,
  carrierStore,
  orderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  formatContactPhone,
  formatPhoneNumber,
} from '@utils/phoneNumberFormat';
import { handleToast } from '@utils/utils';
import { AddNewContact } from '@views/order/components/addNewContact';
import { RatingsForm } from '@views/order/components/models';
import { RATE_CARRIER_ID } from '@views/order/constants';
import { isEmpty } from 'lodash';
import { createMemo, createSignal, Show } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import { carrieContactCardClasses as classes } from '../classes';
import { FieldDisplay } from './FieldDisplay';
import { Rating } from './Rating';
import { SubDetails } from './SubDetails';
import {
  getCarrierContact,
  getContactLevelVerificationIcon,
  getContactLevelVerificationTooltip,
} from './utils';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
  isCarrierSelected: boolean;
};

export const CarrierContact = (props: PropsT) => {
  const filterVendorVerifiedContactsForLoad = isFeatureFlagEnabled(
    FeatureFlags.FilterVendorVerifiedContactsForLoad,
  );
  const newContactModalId = `newContactPopupID-${props.tabIndex}`;

  const rateCarrierModalId = `${RATE_CARRIER_ID}-${props.tabIndex}-rate`;
  const carrierSelectedStyles = classes.iconClasses;

  const handleAddContact = async (
    payload: Record<string, string | number | boolean | null>,
  ) => {
    try {
      const res = await addNewContactToCarrier(
        payload,
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsCarrierContact,
      );

      if (Boolean(res)) {
        addNewContactToCarrierStoreAndUpdateOrderStore(res);
      }
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };
  const isCarrierContactInactive = () => {
    if (props.load.carrierContactId !== props.load.carrierContact?.id) {
      const foundCarrier = carrierStore.carrier
        .filter((x: Carrier | undefined) => x?.id)
        .find((x: Carrier | undefined) => x?.id === props.load.carrierId);
      if (foundCarrier) {
        const foundCarrierContact = foundCarrier.contacts.find(
          (x) => x.id === props.load.carrierContactId,
        );
        if (foundCarrierContact) {
          return foundCarrierContact.status === 'Inactive';
        }
        return false;
      }
      return false;
    }
    return props.load.carrierContact?.status === 'Inactive';
  };
  const getStackClass = () => {
    const baseClass = 'border border-solid rounded-lg border-gray-500 p-2 mb-2';
    return isCarrierContactInactive()
      ? `${baseClass} bg-[#fca5a5]`
      : `${baseClass} bg-white`;
  };

  const shouldRespectVerification = createMemo(() => {
    if (
      (props.load.mode === 'LTL' ||
        !carrierStore.carrier[props.tabIndex]?.isHighwayEnabled ||
        !filterVendorVerifiedContactsForLoad()) &&
      !orderStore.carrierContactLockdownEnabled
    ) {
      return false;
    }

    return true;
  });

  const [verificationAnchorEl, setVerificationAnchorEl] =
    createSignal<Element | null>(null);

  return (
    <Stack>
      <Stack class={getStackClass()}>
        {isCarrierContactInactive() ? (
          <div style={{ padding: '5px 5px 10px 5px' }}>
            <Notification
              type={'error'}
              sxProps={'bg-white'}
              text={'This contact is inactive. Please choose a new one.'}
            />
          </div>
        ) : (
          ''
        )}
        <Stack direction="row" gap={2}>
          <Box flex={1} pt={2}>
            <ContactDropdown
              label="Carrier Load Contact"
              contacts={carrierStore.carrier[props.tabIndex]?.contacts ?? []}
              onChange={(value) => {
                updateLoadPropertyAtIndex({
                  carrierContactId: value,
                });
              }}
              contactId={
                Number(props.load.carrierContactId) ||
                (!isEmpty(carrierStore.carrier[props.tabIndex]?.contacts)
                  ? carrierStore.carrier[props.tabIndex]?.contacts[0]?.id
                  : undefined)
              }
              disabled={!props.isCarrierSelected || orderStore.isReadOnly}
              respectVerification={shouldRespectVerification()}
            />
          </Box>
          <Show
            when={
              getContactLevelVerificationIcon(props.load, props.tabIndex) !== ''
            }
          >
            <Box class="flex items-end pb-1">
              <img
                class="size-4!"
                src={getContactLevelVerificationIcon(
                  props.load,
                  props.tabIndex,
                )}
                onMouseEnter={(event: { currentTarget: Element }) =>
                  setVerificationAnchorEl(event.currentTarget)
                }
                onMouseLeave={() => setVerificationAnchorEl(null)}
              />
              <Popover
                id={`contact-verification-popover-${props.tabIndex}`}
                dataTestId={`contact-verification-popover-${props.tabIndex}`}
                sx={{ 'pointer-events': 'none' }}
                open={Boolean(verificationAnchorEl())}
                anchorEl={verificationAnchorEl()}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                onClose={() => setVerificationAnchorEl(null)}
                disableRestoreFocus
              >
                <Typography variant="body2" class="p-2 max-w-[280px]">
                  {getContactLevelVerificationTooltip(
                    props.load,
                    props.tabIndex,
                  )}
                </Typography>
              </Popover>
            </Box>
          </Show>
          <Box>
            <Button
              variant="contained"
              label="New Contact"
              onClick={() => openModal(newContactModalId)}
              startIcon={<AccountPlusIcon size={20} />}
              disabled={!props.isCarrierSelected || orderStore.isReadOnly}
              class={`${classes.w100} text-armstrong-sm!`}
              dataTestId={formatDataTestId('New Contact', 'button')}
            />
          </Box>
        </Stack>
        <div class="my-2 flex items-center gap-2">
          <InformationIcon
            size={'18px'}
            color="#026EA1"
            style={{ 'margin-top': '2px', flex: '0 0 auto' }}
          />
          <div class="flex flex-col gap-1">
            <p class="text-xs text-[#000000d4] wrap-break-word">
              This contact may not be eligible to receive the Highway Secure
              Rate Confirmation.
            </p>
            <p class="text-xs text-[#000000d4] wrap-break-word">
              When sending a Rate Con, you can choose from the list of emails
              who are configured as eligible recipients.
            </p>
          </div>
        </div>
        <Stack direction="row" gap={3} pt="7px">
          <Box class="min-w-[110px]">
            <FieldDisplay
              startIcon={<PhoneIcon class={carrierSelectedStyles} size={20} />}
              label={
                <a
                  href={`tel: ${formatContactPhone(
                    getCarrierContact(props.load, props.tabIndex),
                    'link',
                  )}`}
                  class="text-armstrong-sm font-medium"
                >
                  {formatContactPhone(
                    getCarrierContact(props.load, props.tabIndex),
                    'display',
                  )}
                </a>
              }
            />
          </Box>
          <Box class="min-w-[110px]">
            <FieldDisplay
              startIcon={<EmailIcon class={carrierSelectedStyles} size={20} />}
              label={
                <a
                  href={`mailto:${
                    carrierStore.carrier[props.tabIndex]?.contacts?.find(
                      (v) => v.id === props.load.carrierContactId,
                    )?.email
                  }`}
                  class={`${classes.anchorStyleClasses} text-armstrong-sm font-medium`}
                >
                  {
                    carrierStore.carrier[props.tabIndex]?.contacts?.find(
                      (v) => v.id === props.load.carrierContactId,
                    )?.email
                  }
                </a>
              }
            />
          </Box>
        </Stack>
        <Box class="mt-1">
          <FieldDisplay
            startIcon={
              <Box class="text-black font-medium text-sm mr-2">Corp Phone</Box>
            }
            label={
              <a
                href={`tel: ${formatPhoneNumber(
                  carrierStore.carrier[props.tabIndex]?.corporatePhoneNumber,
                  'link',
                )}`}
                class="text-sm font-medium"
              >
                {formatPhoneNumber(
                  carrierStore.carrier[props.tabIndex]?.corporatePhoneNumber,
                  'display',
                )}
              </a>
            }
          />
        </Box>
      </Stack>
      <Stack direction="row" gap={1}>
        <Grid item flex={1}>
          <Stack flex={1} gap={1}>
            <div class="mt-1">
              <IconLabel
                label="Address"
                labelClasses="text-xs mb-1"
                contentClasses={classes.smallIconLabelContentClasses}
                icon={<DomainIcon class={carrierSelectedStyles} />}
                content={`${
                  carrierStore.carrier[props.tabIndex]?.address?.line1 || ''
                } ${
                  carrierStore.carrier[props.tabIndex]?.address?.line2 ?? ''
                } ${
                  carrierStore.carrier[props.tabIndex]?.address?.city || ''
                } ${
                  carrierStore.carrier[props.tabIndex]?.address?.state || ''
                } ${carrierStore.carrier[props.tabIndex]?.address?.zip || ''}`}
                textColorCondition={false}
              />
            </div>
            <Box>
              <Rating
                load={props.load}
                tabIndex={props.tabIndex}
                isCarrierSelected={props.isCarrierSelected}
                rateCarrierModalId={rateCarrierModalId}
              />
            </Box>
          </Stack>
        </Grid>
        <Grid item>
          <Stack flex={1} gap={1} maxWidth="180px" minWidth="140px">
            <Box>
              <SubDetails
                name="MC #"
                data={
                  carrierStore.carrier[props.tabIndex]?.mc ?? (
                    <span class="text-[#000000] opacity-40">-</span>
                  )
                }
                isCarrierSelected={props.isCarrierSelected}
              />
            </Box>
            <Box>
              <SubDetails
                name="DOT #"
                data={
                  carrierStore.carrier[props.tabIndex]?.dot ?? (
                    <span class="text-[#000000] opacity-40">-</span>
                  )
                }
                isCarrierSelected={props.isCarrierSelected}
              />
            </Box>
            <Box>
              <SubDetails
                name="Fallouts"
                data={
                  carrierStore.carrier[props.tabIndex]?.fallouts ?? (
                    <span class="text-[#000000] opacity-40">-</span>
                  )
                }
                isCarrierSelected={props.isCarrierSelected}
              />
            </Box>
            <Box>
              <SubDetails
                name="Load Count"
                data={
                  carrierStore.carrier[props.tabIndex]?.totalLoads ?? (
                    <span class="text-[#000000] opacity-40">-</span>
                  )
                }
                isCarrierSelected={props.isCarrierSelected}
              />
            </Box>
          </Stack>
        </Grid>
      </Stack>
      <Box mt={2}>
        <div class="flex w-full gap-x-1">
          <Box flex={1} minWidth={0}>
            <TextField
              multiline
              label="Carrier Instructions"
              rows={8}
              placeholder="This area is for Carrier Instructions that will appear on the Carrier Rate Confirmation and BOL."
              value={props.load.carrierInstructions ?? ''}
              onChange={(value) => {
                updateLoadPropertyAtIndex({
                  carrierInstructions: value,
                });
              }}
              maxLength={800}
              disabled={orderStore.isReadOnly}
              class="invert-padding"
              dataTestId={'carrier-instructions-textareafield'}
            />
          </Box>
          <Show when={props.load.mode === 'LTL'} fallback={<></>}>
            <Box flex={1} minWidth={0}>
              <TextField
                multiline
                label="Terminal Information"
                rows={8}
                placeholder="This area is for Terminal Information that will appear on the BOL."
                value={props.load.terminal}
                onChange={(value) => {
                  updateLoadPropertyAtIndex({
                    terminal: value,
                  });
                }}
                maxLength={500}
                disabled={orderStore.isReadOnly}
                class="invert-padding"
                dataTestId={'terminal-information-textareafield'}
              />
            </Box>
          </Show>
        </div>
      </Box>
      <BasicModal
        id={rateCarrierModalId}
        title="Rate Carrier"
        width={'35vw'}
        header={false}
        onClose={() => closeModal(rateCarrierModalId)}
        footer={false}
        showClose={false}
        modalStyles={{
          background: 'white',
          borderRadius: '5px',
        }}
      >
        <RatingsForm
          onClose={() => closeModal(rateCarrierModalId)}
          modalId={rateCarrierModalId}
        />
      </BasicModal>
      <BasicModal
        id={newContactModalId}
        title="New Contact"
        width="1000px"
        footer={false}
      >
        <AddNewContact
          modalId={newContactModalId}
          initiatePath="OrderDetails"
          onSubmit={handleAddContact}
          useVerified={false}
        />
      </BasicModal>
    </Stack>
  );
};
